import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {StorageService} from '@src/app/core/services/storage.service';
import {ApiService} from '@src/app/shared/services/api.service';

@Injectable({
    providedIn: 'root'
})
export class SettingsService
{
    public pricingEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    public constructor(
        private apiService: ApiService,
        private storageService: StorageService,
    )
    {
        this.bootstrapFromStorage();
    }

    public bootstrap(settings: Array<{ feature?: string, name?: string, payload?: unknown, value?: unknown }> = []): void
    {
        const pricingSetting = settings.find(setting => setting?.feature === 'pricing' && setting?.name === 'pricing_enabled');

        if (!pricingSetting) {
            this.bootstrapFromStorage();
            return;
        }

        this.setPricingEnabled(this.resolveBoolean(pricingSetting.payload ?? pricingSetting.value, true));
    }

    public refresh(): Observable<any>
    {
        return this.apiService.list('settings').pipe(
            tap(response => this.hydrate(response?.data ?? []))
        );
    }

    public hydrate(settings: Array<{ feature?: string, name?: string, payload?: unknown, value?: unknown }> = []): void
    {
        const pricingSetting = settings.find(setting => setting?.feature === 'pricing' && setting?.name === 'pricing_enabled');
        this.setPricingEnabled(this.resolveBoolean(pricingSetting?.value ?? pricingSetting?.payload, true));
    }

    public setPricingEnabled(enabled: boolean): void
    {
        this.pricingEnabled$.next(enabled);
        this.storageService.write('pricing_enabled', JSON.stringify(enabled));
        this.syncTenantSettings(enabled);
    }

    public isPricingEnabled(): boolean
    {
        return this.pricingEnabled$.value;
    }

    public isFeatureEnabled(feature: string): boolean
    {
        if (feature === 'pricing') {
            return this.isPricingEnabled();
        }

        return true;
    }

    public clear(): void
    {
        this.pricingEnabled$.next(true);
        this.storageService.remove('pricing_enabled');
    }

    private bootstrapFromStorage(): void
    {
        this.pricingEnabled$.next(this.resolveBoolean(this.storageService.read('pricing_enabled'), true));
    }

    private resolveBoolean(value: unknown, fallback: boolean): boolean
    {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue.length) {
                return fallback;
            }

            if (trimmedValue === 'true' || trimmedValue === '1') {
                return true;
            }

            if (trimmedValue === 'false' || trimmedValue === '0') {
                return false;
            }

            try {
                return this.resolveBoolean(JSON.parse(trimmedValue), fallback);
            } catch {
                return fallback;
            }
        }

        if (typeof value === 'number') {
            return value === 1;
        }

        return fallback;
    }

    private syncTenantSettings(enabled: boolean): void
    {
        const rawTenant = this.storageService.read('tenant');

        if (!rawTenant) {
            return;
        }

        try {
            const tenant = JSON.parse(rawTenant);
            const settings = Array.isArray(tenant?.settings) ? tenant.settings : [];
            const pricingSettingIndex = settings.findIndex(setting => setting?.feature === 'pricing' && setting?.name === 'pricing_enabled');
            const pricingSetting = {
                feature: 'pricing',
                name: 'pricing_enabled',
                payload: enabled,
            };

            if (pricingSettingIndex === -1) {
                settings.push(pricingSetting);
            } else {
                settings[pricingSettingIndex] = {...settings[pricingSettingIndex], ...pricingSetting};
            }

            tenant.settings = settings;
            this.storageService.write('tenant', JSON.stringify(tenant));
        } catch {
            // Ignore invalid cached tenant data and continue using the dedicated setting cache.
        }
    }
}

